import { AdminExtWebDashboard } from "@coeff/api-admin-ext";
import { useQuery } from "@tanstack/react-query";
import { Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey, SortColumn, SortDirection } from "../../../api";
import {
  AdminTableFooter,
  FlexColumn,
  FlexRow,
  LoaderWithPerfTimings,
  Typography,
} from "../../../components";
import { AdminTable } from "../../../components/AdminTable";
import { COLORS } from "../../../constants";
import { useApiContext } from "../../../utils";
import { PlanLimitUsageBar } from "../../Dashboard/components";
import { ExportCSVButton } from "../../WorkspaceAdmin/ExportCSVButton";
import { TabName } from "../../WorkspaceAdmin/WorkspaceAdmin";

import { DebouncedSearchInput } from "./DebouncedSearchInput";

const Container = styled.div`
  height: 100%;
  width: 100%;
  /* Only two columns (Dashboard, Created By) — constrain to a comfortable width rather than
     stretching across the page. */
  max-width: 680px;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

const PlanUsageSection = styled.div`
  padding-top: 8px;
`;

// Accessible dashboards open in the viewer; the dotted underline signals the name is a link.
// Private dashboards (no admin access path) render as plain greyed text instead.
const DashboardLink = styled.a`
  && {
    color: inherit;
    text-decoration: none;
    border-bottom: 1px dotted ${COLORS.black45};

    &:hover {
      color: ${COLORS.coeblue4};
      border-bottom-color: ${COLORS.coeblue4};
    }
  }
`;

export const WebDashboards: React.FC = () => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const domain = searchParams.get("domain") ?? "all_domains";

  const { adminExtApiClient } = useApiContext();

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);

  const pageSize = 100;

  const [pageOffset, setPageOffset] = useState<number>(0);

  const setPageAndOffset = (page: number) => {
    setPage(page);
    setPageOffset(page * pageSize);
  };

  const onSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setPageAndOffset(0);
  };

  // Fixed default sort (most recent first); interactive sorting is disabled for now.
  const sortedColumn: SortColumn = {
    columnKey: "created_dt",
    value: SortDirection.descend,
  };

  const { data: webDashboardsResponse, isLoading } = useQuery(
    [QueryCacheKey.ADMIN_EXT_WEB_DASHBOARDS, domain, pageSize, pageOffset, sortedColumn, search],
    () =>
      adminExtApiClient.getWebDashboards({
        domain,
        page_size: pageSize,
        offset: pageOffset,
        sort_by: sortedColumn.columnKey,
        sort_order: sortedColumn.value,
        search,
        // Archived dashboards are owner-only and shouldn't surface in the admin list.
        hide_archived: true,
      }),
    {
      enabled: Boolean(domain),
    }
  );

  const dashboards = webDashboardsResponse?.data.web_dashboards ?? [];

  const totalRecords = webDashboardsResponse?.data.total_records ?? 0;

  // The plan limit is only present when the org's subscription has synced one. When absent, the
  // usage bar renders nothing.
  const planLimit = webDashboardsResponse?.data.plan_limit ?? null;

  // Family-wide active count (not the filtered list total) — the correct usage-bar numerator.
  const planUsageCount = webDashboardsResponse?.data.plan_usage_count ?? 0;

  const gracePeriodEndsDt = webDashboardsResponse?.data.grace_period_ends_dt ?? null;

  const columns: ColumnsType<AdminExtWebDashboard> = [
    {
      title: (
        <Typography noWrap color="black">
          Dashboard
        </Typography>
      ),
      dataIndex: "dashboard_name",
      key: "dashboard_name",
      width: 420,
      render: (dashboardName: AdminExtWebDashboard["dashboard_name"], dashboard) => {
        const name = dashboardName || "Untitled dashboard";

        // The row is a clickable link only when the admin has an access path to the dashboard
        // (shared with them explicitly, or domain-/public-accessible). Otherwise it's greyed out
        // and not clickable — the admin has no way to open it.
        const nameEl =
          dashboard.can_admin_access && dashboard.web_dashboard_id ? (
            <DashboardLink
              href={`/dashboards/${dashboard.web_dashboard_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Typography noWrap fontWeight={500}>
                {name}
              </Typography>
            </DashboardLink>
          ) : (
            <Typography noWrap fontWeight={500}>
              {name}
            </Typography>
          );

        const row = (
          <FlexColumn
            gap={0}
            minWidthZero
            style={{ maxWidth: 400, opacity: dashboard.can_admin_access ? 1 : 0.5 }}
          >
            {nameEl}
          </FlexColumn>
        );

        return dashboard.can_admin_access ? (
          <Tooltip title={name}>{row}</Tooltip>
        ) : (
          <Tooltip title="Dashboard sharing: Private">{row}</Tooltip>
        );
      },
    },
    {
      title: (
        <Typography noWrap color="black">
          Created By
        </Typography>
      ),
      dataIndex: "created_by",
      key: "created_by",
      width: 260,
      render: (createdBy: AdminExtWebDashboard["created_by"], dashboard) => (
        <div style={{ maxWidth: 240, opacity: dashboard.can_admin_access ? 1 : 0.5 }}>
          <Typography title={dashboard.created_by_email} noWrap>
            {createdBy}
          </Typography>
        </div>
      ),
    },
  ];

  return (
    <Container>
      {planLimit != null && planLimit > 0 && (
        <PlanUsageSection>
          <PlanLimitUsageBar
            used={planUsageCount}
            limit={planLimit}
            meter={{ gracePeriodEndsDt }}
          />
        </PlanUsageSection>
      )}

      <FlexColumn gap={0.5} style={{ overflow: "hidden", marginTop: 16 }}>
        <FlexRow style={{ justifyContent: "space-between", marginBottom: 12 }}>
          <DebouncedSearchInput initialSearch={search} onChange={onSearchChange} />

          <ExportCSVButton tab={TabName.web_dashboards} />
        </FlexRow>

        <AdminTable
          columns={columns}
          dataSource={dashboards.map(dashboard => ({
            key: dashboard.web_dashboard_id,
            ...dashboard,
          }))}
          loading={false}
          locale={{
            emptyText: isLoading ? (
              <LoaderWithPerfTimings name="WebDashboards.locale" />
            ) : (
              "No dashboards found"
            ),
          }}
          rowKey={r => r.web_dashboard_id}
          pagination={false}
        />

        <div style={{ marginTop: 12 }}>
          <AdminTableFooter
            page={page}
            pageSize={pageSize}
            onPageChange={page => setPageAndOffset(page)}
            data={dashboards.map(d => [d.dashboard_name ?? "", true])}
            totalRowCount={totalRecords}
          />
        </div>
      </FlexColumn>
    </Container>
  );
};
